import { Button, Form, notification, Row, Switch, Typography } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import StockControlTable from "./components/StockControlTable";
import { useDateRange } from "context/DateRangeContext";
import { useStockControl } from "hooks/stock/useStockControl";
import StockControlTableByPeriod from "./components/StockControlTableByPeriod";
import { FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import { RequestGetInventoryControlDetails } from "api/generated";
import ManualVolumeRegisterDrawer from "routes/volumeMeasurerRegistries/components/ManualVolumeRegistrerDrawer";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { RouteComponentProps, withRouter } from "react-router-dom";
import LateArrivingInformationAlert from "./components/LateArrivingInformationAlert";
import DistributionPointSelected from "context/MyDistributionPoint";
import { useIsMobile } from "hooks/useIsMobile";
import { FormItem } from "common/components/form/FormItem";

function StockControl(props: RouteComponentProps) {
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { isMobile } = useIsMobile();
  const [showByDay, setShowByDay] = useState(true);

  // ESTADO PARA MEDICIONES MANUALES
  const [showAddMesurement, setShowAddMesurement] = useState(false);
  const [measurementDate, setMeasurementDate] = useState<moment.Moment>();

  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();

  const pagination =
    usePaginationTable<RequestGetInventoryControlDetails>(props);

  const {
    loading: fetchLoading,
    error,
    recalculateGic,
    recalculatePeriod,
    upsertCauseAndSolutionOnDifference,
    getLastMeasurement,
    downloadStockControl,
    reloadTables,
  } = useStockControl({
    startDate,
    endDate,
    fetchByDay: showByDay,
    pagination,
  });

  const loading = useMemo(() => {
    return fetchLoading;
  }, [fetchLoading]);

  /**
   * Maneja el cambio de estado del Switch entre vista por día o por período
   * @param {boolean} checked - Indica si el switch está activado (vista por día) o desactivado (vista por período)
   */
  function handleSwitchChange(checked: boolean) {
    setShowByDay(checked);
    pagination.setCurrentPage(0);
    // Cuando cambia a modo día, resetear al mes actual
    if (checked) {
      const newRange: [dayjs.Dayjs, dayjs.Dayjs] = [
        rangeTimeValue[0].startOf("month"),
        rangeTimeValue[0].endOf("month"),
      ];
      setRangeTimeValue(newRange);
    } else {
      pagination.setPageSize(10);
    }
  }

  /**
   * Efecto que muestra una notificación de error cuando ocurre un problema en la carga de datos
   * @param {string} error - Mensaje de error a mostrar en la notificación
   */
  useEffect(() => {
    if (error) {
      notification.error({
        message: error,
      });
    }
  }, [error]);

  function handleRegisterManualMeasurement(dateToRegister: moment.Moment) {
    setMeasurementDate(dateToRegister.clone().endOf("month"));
    setShowAddMesurement(true);
  }

  return (
    <div>
      <Form layout="vertical">
        <Row gutter={24}>
          <ResponsiveCol size="xl">
            <Typography.Title>{distributionPoint?.name}</Typography.Title>
          </ResponsiveCol>
          <ResponsiveCol size="lg">
            <FormItem label="Periodo de Control de Existencias">
              <DateRangePicker
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
                config={{
                  allowedTypes: [DateFilterType.MONTH, DateFilterType.RANGE],
                  forceType: showByDay
                    ? DateFilterType.MONTH
                    : DateFilterType.RANGE,
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="lg">
            <FormItem>
              <Switch
                style={{ float: "right" }}
                checkedChildren="Resumen por Día"
                unCheckedChildren="Resumen por Periodo"
                value={showByDay}
                onChange={handleSwitchChange}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xl">
            <FormItem>
              <Button
                variant="outlined"
                color="blue"
                icon={<FileExcelOutlined />}
                style={{ float: "right" }}
                loading={loading}
                disabled={pagination.totalItems === 0 || loading}
                onClick={() => {
                  downloadStockControl();
                }}
              >
                Descargar reporte
                {!isMobile ? (showByDay ? " diario" : " del periodo") : ""}
              </Button>
            </FormItem>
          </ResponsiveCol>
        </Row>
        <Row>
          <ResponsiveCol size="xl">
            {distributionPoint?.id && (
              <LateArrivingInformationAlert
                isLoading={loading}
                onReload={() => {
                  reloadTables();
                }}
                startDate={startDate}
                endDate={endDate}
                distributionPointIds={[distributionPoint.id]}
              />
            )}
          </ResponsiveCol>
        </Row>
        <Row gutter={24}>
          {showByDay && (
            <StockControlTable
              pagination={pagination}
              loading={loading}
              recalculateGic={recalculateGic}
              upsertCauseAndSolutionOnDifference={
                upsertCauseAndSolutionOnDifference
              }
              getLastMeasurement={getLastMeasurement}
              onRegisterManualMeasurement={handleRegisterManualMeasurement}
            />
          )}
          {!showByDay && (
            <StockControlTableByPeriod
              pagination={pagination}
              loading={loading}
              recalculatePeriod={recalculatePeriod}
              onRegisterManualMeasurement={handleRegisterManualMeasurement}
            />
          )}
        </Row>
      </Form>
      {measurementDate && (
        <ManualVolumeRegisterDrawer
          isVisible={showAddMesurement}
          onClose={(update) => {
            if (update) {
              if (showByDay) {
                recalculateGic(measurementDate);
              } else {
                recalculatePeriod(measurementDate);
              }
            }
            setShowAddMesurement(false);
          }}
          defaultDate={dayjs(measurementDate.valueOf())}
        />
      )}
    </div>
  );
}
export default withRouter(StockControl);
